import { Box, Paper, Stack, SxProps, Theme } from '@mui/material';
import FilterChip from './FilterChip';
import ClearAllButton from './ClearAllButton';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTransaction } from '../../../context/TransactionContext';
import { TransactionTypeValue } from '../../../services/transaction.types';
import Account from './Account';
import Asset from './Asset';
import DateRangeFilter from './DateRangeFilter';
import Flagged from './Flagged';
import Search from './Search';
import TypeFilter from './Type';
import { FilterValues, FilterKeys, FilterUpdatePayload, FilterInstructionAction } from '../../../types/filter.types';

// Type guard to check if a value is an instruction object
function isInstructionAction<T>(value: any): value is FilterInstructionAction<T> {
    return typeof value === 'object' && value !== null && ('add' in value || 'remove' in value || 'clear' in value || 'set' in value);
}

interface FilterPanelProps {
    onFiltersChange: (filters: FilterValues) => void;
    initialFilters?: FilterValues;
    sx?: SxProps<Theme>;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
    onFiltersChange,
    initialFilters = {},
    sx
}) => {
    const { t } = useTranslation();
    const [activeFilters, setActiveFilters] = React.useState<FilterValues>(initialFilters);
    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = React.useState(false);

    const { userTypes: transactionTypes, transactionIssues, accounts, userCoins: assets, allTimeIssueCount } = useTransaction();

    const handleFilterChange = useMemo(
        () => (newFilters: FilterUpdatePayload) => { 
            setActiveFilters((prev) => {
                let mergedFilters = { ...prev }; 

                Object.keys(newFilters).forEach((keyStr: string) => {
                    const key = keyStr as FilterKeys;
                    const instructionOrValue = newFilters[key as keyof FilterUpdatePayload];

                    if (isInstructionAction(instructionOrValue)) {
                        const instruction = instructionOrValue;

                        if (instruction.clear) {
                            if (key === 'dates') {
                                mergedFilters.dates = { start: null, end: null };
                            } else {
                                delete mergedFilters[key];
                            }
                        } else if (instruction.set !== undefined) {
                            // Replace the value directly
                            // If setting to null, undefined, or empty array, remove the key (unless it's dates)
                            const valueToSet = instruction.set;
                            const isEmptyArray = Array.isArray(valueToSet) && valueToSet.length === 0;
                            const isNullOrUndefined = valueToSet === null || valueToSet === undefined;

                            if ((isNullOrUndefined || isEmptyArray) && key !== 'dates') {
                                delete mergedFilters[key];
                            } else {
                                mergedFilters[key] = valueToSet as any;
                            }
                        } else if (instruction.add || instruction.remove) {
                            // Ensure we are working with an array for add/remove
                            let currentArray = Array.isArray(mergedFilters[key]) ? [...(mergedFilters[key] as any[])] : [];

                            if (instruction.add) {
                                // Add items next (preventing duplicates)
                                const itemsToAdd = instruction.add.filter(
                                    item => !currentArray.some(existing => String(existing) === String(item))
                                );
                                // Handle TransactionTypeValue specifically if needed (casting might be required)
                                if (key === 'types') {
                                    currentArray = [...currentArray, ...(itemsToAdd as TransactionTypeValue[])];
                                } else {
                                    currentArray = [...currentArray, ...(itemsToAdd as string[])];
                                }
                            }

                            if (instruction.remove) {
                                const itemsToRemove = new Set(instruction.remove.map(String)); 
                                currentArray = currentArray.filter(
                                    (item) => !itemsToRemove.has(String(item))
                                );
                            }

                            if (currentArray.length > 0) {
                                switch (key) {
                                    case 'types':
                                        mergedFilters.types = currentArray as TransactionTypeValue[];
                                        break;
                                    case 'accounts':
                                    case 'assets':
                                    case 'flags':
                                        mergedFilters[key] = currentArray as string[];
                                        break;
                                    // No default needed as key is constrained by FilterKeys
                                }
                            } else {
                                // If array is empty after add/remove, delete the key
                                delete mergedFilters[key];
                            }
                        }
                    } else if (instructionOrValue !== undefined && instructionOrValue !== null) {
                        // Handle direct value assignment if not an instruction
                        // e.g., { search: 'some text' } (legacy or simple updates)
                        // Ensure empty strings for search are handled (remove key)
                        if (key === 'search' && instructionOrValue === '') {
                            delete mergedFilters[key];
                        } else {
                            mergedFilters[key] = instructionOrValue as any;
                        }
                    } else if (instructionOrValue === null || instructionOrValue === undefined) {
                        // If the payload explicitly sets a key to null/undefined, remove it (unless dates)
                        if (key !== 'dates') {
                            delete mergedFilters[key];
                        } else {
                            // Set dates to null object if explicitly set to null/undefined
                            mergedFilters.dates = { start: null, end: null };
                        }
                    }
                });

                Object.keys(mergedFilters).forEach((keyStr) => {
                    const key = keyStr as FilterKeys;
                    const value = mergedFilters[key];

                    if (Array.isArray(value) && value.length === 0) {
                        delete mergedFilters[key];
                    } else if (value === undefined || value === null) { // Handles undefined/null for any key
                        delete mergedFilters[key];
                    } else if (key === 'dates') {
                        const dateValue = value as FilterValues['dates']; // Cast to the expected type for dates
                        // Check if it's an object and both start/end are missing or null/undefined
                        if (typeof dateValue === 'object' && dateValue !== null && !dateValue.start && !dateValue.end) {
                            delete mergedFilters[key];
                        }
                    }
                });

                // Only update if filters actually changed
                if (JSON.stringify(prev) === JSON.stringify(mergedFilters)) {
                    return prev;
                }

                // Notify parent of changes
                onFiltersChange(mergedFilters);
                return mergedFilters;
            });
        },
        [onFiltersChange]
    );

    const handleClearFilter = useMemo(
        () => (filterType: FilterKeys, value?: string) => {
            setActiveFilters((prev) => {
                const newFilters = { ...prev };
                if (value !== undefined) {
                    if (filterType === 'dates') {
                        delete newFilters[filterType];
                    } else if (
                        filterType === 'types' ||
                        filterType === 'flags' ||
                        filterType === 'assets' ||
                        filterType === 'accounts'
                    ) {
                        // Handle types separately since it's TransactionTypeValue[]
                        if (filterType === 'types') {
                            const filterArray = newFilters[filterType] as TransactionTypeValue[];
                            if (filterArray) {
                                newFilters[filterType] = filterArray.filter((v) => v.toString() !== value);
                                if ((newFilters[filterType] as TransactionTypeValue[]).length === 0) {
                                    delete newFilters[filterType];
                                }
                            }
                        } else {
                            // Handle string arrays
                            const filterArray = newFilters[filterType] as string[];
                            if (filterArray) {
                                newFilters[filterType] = filterArray.filter((v) => v !== value);
                                if ((newFilters[filterType] as string[]).length === 0) {
                                    delete newFilters[filterType];
                                }
                            }
                        }
                    } else if (newFilters[filterType] === value) {
                        delete newFilters[filterType];
                    }
                } else {
                    delete newFilters[filterType];
                }

                // Only update if filters actually changed
                if (JSON.stringify(prev) === JSON.stringify(newFilters)) {
                    return prev;
                }

                // Notify parent of changes
                onFiltersChange(newFilters);
                return newFilters;
            });
        },
        [onFiltersChange]
    );

    const handleClearAll = useMemo(
        () => () => {
            setActiveFilters((prev) => {
                if (Object.keys(prev).length === 0) {
                    return prev;
                }
                onFiltersChange({});
                return {};
            });
        },
        [onFiltersChange]
    );

    const getFilterLabel = (filterType: FilterKeys, value: string | number) => {
        switch (filterType) {
            case 'types':
                const type = transactionTypes.find((t) => t.value.toString() === value.toString());
                return type ? type.name : value;

            case 'accounts':
                return value; // Account names are used directly

            case 'assets':
                const asset = assets?.find((a) => a.ticker === value);
                return asset ? asset.name : value;

            case 'flags':
                const issue = transactionIssues.find((i) => i.id === value);
                return issue ? issue.description : value;

            default:
                return value;
        }
    };

    const renderActiveFilters = () => {
        const chips = [];

        // Search filter
        if (activeFilters.search) {
            chips.push(
                <FilterChip
                    key="search"
                    label={activeFilters.search}
                    onDelete={() => handleClearFilter('search')}
                />
            );
        }

        // Transaction type filters
        activeFilters.types?.forEach((type) => {
            if (type !== undefined && type !== null) {
                chips.push(
                    <FilterChip
                        key={`type-${type}`}
                        label={getFilterLabel('types', type)}
                        onDelete={() => handleClearFilter('types', type.toString())}
                    />
                );
            }
        });

        // Account filters
        activeFilters.accounts?.forEach((account) => {
            if (account) {
                chips.push(
                    <FilterChip
                        key={`account-${account}`}
                        label={getFilterLabel('accounts', account)}
                        onDelete={() => handleClearFilter('accounts', account)}
                    />
                );
            }
        });

        // Asset filters
        activeFilters.assets?.forEach((asset) => {
            if (asset) {
                chips.push(
                    <FilterChip
                        key={`asset-${asset}`}
                        label={getFilterLabel('assets', asset)}
                        onDelete={() => handleClearFilter('assets', asset)}
                    />
                );
            }
        });

        // Flag filters
        activeFilters.flags?.forEach((flag) => {
            if (flag) {
                chips.push(
                    <FilterChip
                        key={`flag-${flag}`}
                        label={getFilterLabel('flags', flag)}
                        onDelete={() => handleClearFilter('flags', flag)}
                    />
                );
            }
        });

        // Date filter
        if (activeFilters.dates && (activeFilters.dates.start || activeFilters.dates.end)) {
            const formatDate = (dateStr: any) => {
                if (!dateStr) return 'Any';
                // Simple date formatting that works for both string and Date objects
                const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
                return date.toLocaleDateString();
            };

            const dateLabel = `${formatDate(activeFilters.dates.start)} - ${formatDate(activeFilters.dates.end)}`;
            chips.push(
                <FilterChip
                    key="dates"
                    label={dateLabel}
                    onDelete={() => handleClearFilter('dates')}
                />
            );
        }

        return chips;
    };

    const hasActiveFilters = Object.keys(activeFilters).length > 0;

    return (
        <Paper 
            sx={{
                ...sx,
                boxShadow: 'none',
                borderRadius: 0,
                bgcolor: 'background.paper',
                border: 'none'
            }}
            elevation={0}
        >
            <Box sx={{
                p: 2,
                pb: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                width: '100%',
                mb: 0
            }}>
                <Box sx={{ width: '100%' }}>
                    <Stack 
                        direction={{ xs: 'column', md: 'row' }} 
                        spacing={2} 
                        alignItems="flex-start" 
                        sx={{ width: '100%' }}
                    >
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            width: '100%',
                            gap: 2
                        }}>
                            <Search onFiltersChange={(value) => {
                                handleFilterChange(value);
                            }} 
                            value={activeFilters.search}
                            onDropdownStateChange={setIsSearchDropdownOpen} 
                            activeFilters={activeFilters}
                            />
                            <TypeFilter
                                types={transactionTypes || []}
                                value={activeFilters.types}
                                onChange={(value) => handleFilterChange({ types: { set: value } })}
                            />
                            <Account
                                accounts={accounts}
                                value={activeFilters.accounts}
                                onChange={(value) => handleFilterChange({ accounts: { set: value } })}
                            />
                            <Asset
                                value={activeFilters.assets}
                                onChange={(value) => handleFilterChange({ assets: { set: value } })}
                            />
                            <Flagged
                                value={activeFilters.flags}
                                onChange={(value) => handleFilterChange({ flags: { set: value } })}
                                transactionIssues={transactionIssues}
                                allTimeIssueCount={allTimeIssueCount ?? 0}
                            />
                            <DateRangeFilter
                                value={activeFilters.dates} 
                                onChange={(value) => handleFilterChange({ dates: { set: value } })} 
                            />
                        </Box>
                    </Stack>

                    {/* Active Filters */}
                    {hasActiveFilters && (
                        <Box sx={{
                            mt: { xs: 8, md: 1 }, // More margin on mobile, less on desktop
                            transition: 'all 0.2s ease-in-out',
                            marginLeft: { 
                                xs: 0, // No margin on small screens
                                md: isSearchDropdownOpen ? '250px' : '0' // Apply conditionally on medium and up
                            },
                        }}>
                            <Stack direction="row" spacing={1} sx={{
                                flexWrap: 'wrap',
                                alignItems: 'center'
                            }}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        flexWrap: 'wrap',
                                        gap: 1
                                    }}
                                >
                                    {renderActiveFilters()}
                                    <ClearAllButton 
                                        onClick={handleClearAll}
                                        label={t('transactions.filters.clearAll')}
                                    />
                                </Stack>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default FilterPanel;
